/**
 * LanguageSelector - Global language selector for the header
 *
 * A compact dropdown that allows users to switch the display language
 * for translated content throughout the application.
 */

import React from 'react';
import { useLanguage, SUPPORTED_LANGUAGES } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Globe, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LanguageSelectorProps {
  compact?: boolean;
  showLabel?: boolean;
  className?: string;
}

export function LanguageSelector({
  compact = false,
  showLabel = true,
  className
}: LanguageSelectorProps) {
  const { language, setLanguage, languageInfo, loading } = useLanguage();

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled className={className}>
        <Globe className="h-4 w-4 animate-pulse" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'flex items-center gap-2',
            compact ? 'px-2' : 'px-3',
            className
          )}
        >
          <span className="text-base" role="img" aria-label={languageInfo?.name}>
            {languageInfo?.flag}
          </span>
          {showLabel && !compact && (
            <span className="text-sm font-medium">
              {languageInfo?.code.toUpperCase()}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
          Display Language
        </div>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={cn(
              'flex items-center justify-between cursor-pointer',
              language === lang.code && 'bg-accent'
            )}
          >
            <div className="flex items-center gap-3">
              <span className="text-base" role="img" aria-label={lang.name}>
                {lang.flag}
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{lang.name}</span>
                <span className="text-xs text-muted-foreground">{lang.native}</span>
              </div>
            </div>
            {language === lang.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
